import React, { useCallback, useEffect, useState } from 'react';
import { useNavigation } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { ListActionSelect } from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';

import { CREDIT_CARDS_ICONS } from '../../../../../constants/credit-cards';
import type { CardType } from '../../../../../graphql/types';
import type { ModalStackParamList } from '../../../../../navigation/AppNavigation.props';
import type { CreditCardOption } from '../../../Checkout.utils';
import { useCheckoutContext } from '../../../CheckoutProvider';
import { useCreditCardOptions } from '../../../hooks';

export const ConnectedCheckoutPaymentBlockCreditCardPicker = () => {
  const context = useCheckoutContext();

  return <CheckoutPaymentBlockCreditCardPicker {...context} />;
};

export const CheckoutPaymentBlockCreditCardPicker = (
  props: Pick<
    ReturnType<typeof useCheckoutContext>,
    | 'customer'
    | 'payload'
    | 'checkoutFieldsValidation'
    | 'handleCreditCardSelected'
    | 'resetCheckoutFieldValidation'
  >,
) => {
  const {
    customer,
    payload,
    checkoutFieldsValidation,
    handleCreditCardSelected,
    resetCheckoutFieldValidation,
  } = props;
  const { creditCardOptions } = useCreditCardOptions(customer);
  const creditCard = payload?.billingAccountId;

  const { t } = useLocalizationContext();
  const navigation =
    useNavigation<NativeStackNavigationProp<ModalStackParamList>>();

  // Credit card from options by value.
  const selectedCard = useCardByValueOrFirst(creditCard, creditCardOptions);
  const selectedCardIcon = CREDIT_CARDS_ICONS[selectedCard?.cardType];

  // Automatically select first credit card if none are selected.
  useEffect(() => {
    if (creditCard !== 'ADD' || !selectedCard) return;
    handleCreditCardSelected(selectedCard.value);
  }, [selectedCard, creditCard, handleCreditCardSelected]);

  // Parent callback.
  const handleSelectedCardChange = useCallback(
    (cardId: string | number) => {
      resetCheckoutFieldValidation('billingAccountId');
      handleCreditCardSelected(cardId as string);
      const isAddCardOptionSelected = cardId === 'ADD';

      if (isAddCardOptionSelected) navigation.navigate('PaymentMethodAdd');
    },
    [navigation, resetCheckoutFieldValidation, handleCreditCardSelected],
  );
  // ────────────────────────

  const isSelectCardFieldValid = checkoutFieldsValidation.billingAccountId;
  const selectCardFieldNotice = isSelectCardFieldValid
    ? undefined
    : t('checkout.required-field');

  // ────────────────────────
  return (
    <ListActionSelect
      title=""
      iconName={selectedCardIcon}
      value={selectedCard.value}
      onSelect={handleSelectedCardChange}
      options={creditCardOptions}
      notice={selectCardFieldNotice}
      noticePalette="caution"
    />
  );
};

// If we already have selected a credit card, use that one.
// Otherwise, use the second one, since the first is "Add card".
// If there are no available options, use the "Add card" option.
const useCardByValueOrFirst = (
  creditCard: string,
  creditCardOptions: readonly CreditCardOption[],
) => {
  const { t } = useLocalizationContext();
  const [selectedCard, setSelectedCard] = useState({
    value: 'ADD',
    label: t('checkout.add-card'),
    cardType: 'NEW' as CardType | 'NEW',
  });

  useEffect(() => {
    const cardToFind = creditCard === 'ADD' ? '' : creditCard;

    setSelectedCard(
      creditCardOptions.find(({ value }) => value === cardToFind) ??
        creditCardOptions[1] ??
        creditCardOptions[0],
    );
  }, [creditCardOptions, creditCard, setSelectedCard]);

  return selectedCard;
};
